import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import {
  ArrowLeft, Plus, Loader, Edit2, Trash2, Eye, EyeOff,
  Sparkles, ExternalLink
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast, Toaster } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogAdmin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateTopic, setGenerateTopic] = useState('');
  const [form, setForm] = useState({
    title: '', slug: '', content: '', excerpt: '',
    meta_title: '', meta_description: '', tags: '', published: false
  });

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/dashboard'); return; }
    fetchPosts();
  }, [user]);

  const fetchPosts = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/blog/admin/posts`, { withCredentials: true });
      setPosts(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (post = null) => {
    if (post) {
      setEditingPost(post);
      setForm({
        title: post.title, slug: post.slug, content: post.content,
        excerpt: post.excerpt || '', meta_title: post.meta_title || '',
        meta_description: post.meta_description || '',
        tags: (post.tags || []).join(', '), published: post.published
      });
    } else {
      setEditingPost(null);
      setForm({ title: '', slug: '', content: '', excerpt: '', meta_title: '', meta_description: '', tags: '', published: false });
    }
    setShowEditor(true);
  };

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  const handleSave = async () => {
    if (!form.title || !form.content) { toast.error('Title and content required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: form.slug || generateSlug(form.title),
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean)
      };
      if (editingPost) {
        await axios.put(`${BACKEND_URL}/api/blog/admin/posts/${editingPost._id}`, payload, { withCredentials: true });
        toast.success('Post updated!');
      } else {
        await axios.post(`${BACKEND_URL}/api/blog/admin/posts`, payload, { withCredentials: true });
        toast.success('Post created!');
      }
      setShowEditor(false);
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/blog/admin/posts/${postId}`, { withCredentials: true });
      toast.success('Post deleted');
      fetchPosts();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleGenerate = async () => {
    if (!generateTopic.trim()) { toast.error('Enter a topic'); return; }
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/blog/admin/generate`,
        { topic: generateTopic },
        { withCredentials: true }
      );
      setForm({
        title: data.title || '', slug: data.slug || '',
        content: data.content || '', excerpt: data.excerpt || '',
        meta_title: data.meta_title || '', meta_description: data.meta_description || '',
        tags: (data.tags || []).join(', '), published: false
      });
      setEditingPost(null);
      setShowEditor(true);
      setGenerateTopic('');
      toast.success('Blog post generated! Review and publish.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const togglePublish = async (post) => {
    try {
      await axios.put(`${BACKEND_URL}/api/blog/admin/posts/${post._id}`, { published: !post.published }, { withCredentials: true });
      fetchPosts();
      toast.success(post.published ? 'Post unpublished' : 'Post published!');
    } catch (err) {
      toast.error('Failed');
    }
  };

  return (
    <div className="min-h-screen bg-[var(--main-bg)] p-4 sm:p-8" data-testid="blog-admin">
      <Toaster position="top-right" />
      <div className="max-w-5xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--obsidian)] mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-sm font-medium">Back to Dashboard</span>
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <h1 className="text-3xl sm:text-5xl font-black tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Blog Manager
          </h1>
          <button
            onClick={() => openEditor()}
            data-testid="new-post-btn"
            className="bg-[var(--obsidian)] text-white font-bold px-6 py-3 border-2 border-[var(--border-default)] hover:translate-x-[2px] hover:translate-y-[2px] transition-transform flex items-center gap-2"
          >
            <Plus size={16} /> New Post
          </button>
        </div>

        {/* AI Generator */}
        <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 mb-8">
          <h3 className="font-bold mb-3 flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <Sparkles size={18} className="text-[var(--secondary)]" /> AI Blog Generator
          </h3>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              value={generateTopic}
              onChange={(e) => setGenerateTopic(e.target.value)}
              placeholder='e.g. "10 Ways to Organize Your Ideas"'
              className="flex-1 min-w-0 p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm"
              data-testid="blog-topic-input"
            />
            <button
              onClick={handleGenerate}
              disabled={generating}
              data-testid="generate-blog-btn"
              className="bg-[var(--secondary)] text-white font-bold px-6 py-3 border-2 border-[var(--border-default)] disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap shrink-0"
            >
              {generating ? <><Loader size={16} className="animate-spin" /> Generating...</> : <><Sparkles size={16} /> Generate</>}
            </button>
          </div>
        </div>

        {/* Post List */}
        {loading ? (
          <div className="flex justify-center p-12"><Loader size={24} className="animate-spin" /></div>
        ) : posts.length === 0 ? (
          <div className="border-2 border-[var(--border-default)] p-12 text-center">
            <p className="text-[var(--text-secondary)]">No blog posts yet. Create one or use the AI generator!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map((post) => (
              <div key={post._id} className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-5 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full ${post.published ? 'bg-green-500' : 'bg-[var(--text-muted)]'}`}></span>
                    <h3 className="font-bold text-sm truncate">{post.title}</h3>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[var(--text-muted)]">
                    <span>/blog/{post.slug}</span>
                    <span>{post.views || 0} views</span>
                    <span>{post.published ? 'Published' : 'Draft'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => togglePublish(post)} className="p-2 border border-[var(--border-default)] hover:bg-[var(--surface-alt)] transition-colors" title={post.published ? 'Unpublish' : 'Publish'}>
                    {post.published ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 border border-[var(--border-default)] hover:bg-[var(--surface-alt)] transition-colors">
                    <ExternalLink size={14} />
                  </a>
                  <button onClick={() => openEditor(post)} className="p-2 border border-[var(--border-default)] hover:bg-[var(--surface-alt)] transition-colors">
                    <Edit2 size={14} />
                  </button>
                  <button onClick={() => handleDelete(post._id)} className="p-2 border border-[var(--border-default)] hover:bg-[var(--primary)] hover:text-white transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Editor Dialog */}
        <Dialog open={showEditor} onOpenChange={setShowEditor}>
          <DialogContent className="w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto bg-[var(--surface)] border border-[var(--border-default)] p-0" style={{ borderRadius: 0 }}>
            <DialogHeader className="p-6 pb-4 border-b border-[var(--border-default)]">
              <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {editingPost ? 'Edit Post' : 'New Post'}
              </DialogTitle>
              <DialogDescription className="text-sm text-[var(--text-secondary)]">Write or edit blog content</DialogDescription>
            </DialogHeader>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold mb-1">Title</label>
                <input value={form.title} onChange={(e) => { setForm({...form, title: e.target.value, slug: form.slug || generateSlug(e.target.value)}); }}
                  className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm" data-testid="post-title-input" />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1">Slug</label>
                <input value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})}
                  className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm" />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1">Excerpt</label>
                <textarea value={form.excerpt} onChange={(e) => setForm({...form, excerpt: e.target.value})}
                  rows={2} className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm resize-none" />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1">Content</label>
                <div className="border-2 border-[var(--border-default)]">
                  <ReactQuill
                    theme="snow"
                    value={form.content}
                    onChange={(val) => setForm({...form, content: val})}
                    data-testid="post-content-input"
                    style={{ minHeight: '250px' }}
                    modules={{
                      toolbar: [
                        [{ 'header': [2, 3, false] }],
                        ['bold', 'italic', 'underline'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        ['link'],
                        ['clean']
                      ]
                    }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold mb-1">Meta Title (SEO)</label>
                  <input value={form.meta_title} onChange={(e) => setForm({...form, meta_title: e.target.value})}
                    className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1">Tags (comma separated)</label>
                  <input value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})}
                    className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold mb-1">Meta Description (SEO)</label>
                <textarea value={form.meta_description} onChange={(e) => setForm({...form, meta_description: e.target.value})}
                  rows={2} className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm resize-none" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({...form, published: e.target.checked})} className="w-4 h-4" />
                <span className="text-sm font-bold">Publish immediately</span>
              </label>
              <button onClick={handleSave} disabled={saving} data-testid="save-post-btn"
                className="w-full bg-[var(--obsidian)] text-white font-bold px-6 py-3 border-2 border-[var(--border-default)] disabled:opacity-50">
                {saving ? 'Saving...' : editingPost ? 'Update Post' : 'Create Post'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default BlogAdmin;
